'use client';

import React, { useState, useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { useQueryClient } from '@tanstack/react-query';
import { useCurrentUser } from '@/contexts/authentication/client';
import { useCharacters, useStoryCharacters } from '@/contexts/characters/hooks';
import { characterKeys } from '@/contexts/characters/queryKeys';
import { copyCharacterFromStory } from '@/contexts/characters/actions/copyCharacterFromStory';
import { type Character } from '@/contexts/characters/types';
import { CharacterCard } from '@/contexts/characters/components/CharacterCard';
import { CharacterForm } from '@/contexts/characters/components/CharacterForm';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Alert, AlertDescription } from '@/shared/components/ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/shared/components/ui/dialog';
import { ListFilters, useSearchFilter, SortDropdown } from '@/shared/components/list';
import type { SortOption } from '@/shared/components/list';
import { Loader2, Plus, Search, BookOpen, X, Check } from 'lucide-react';

interface CharacterLibraryClientProps {
  locale: string;
}

export function CharacterLibraryClient({ locale }: CharacterLibraryClientProps) {
  const t = useTranslations('stories.characters');
  const { user } = useCurrentUser();
  const familyId = user?.familyId ?? undefined;
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingCharacter, setEditingCharacter] = useState<Character | null>(null);
  // "Add from a story" promote picker (story characters are internal projections,
  // no longer a browsable library tab — they're only a SOURCE to copy from).
  const [storyPickerOpen, setStoryPickerOpen] = useState(false);
  const [copyingId, setCopyingId] = useState<string | null>(null);
  const [copiedIds, setCopiedIds] = useState<Set<string>>(new Set());
  const queryClient = useQueryClient();
  const [storyVisible, setStoryVisible] = useState(20);
  const [storySearch, setStorySearch] = useState('');

  // Filters (library tab only)
  const { searchValue, debouncedValue: searchQuery, setSearchValue } = useSearchFilter();
  const [sortBy, setSortBy] = useState<'createdAt' | 'usageCount' | 'name'>('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const { data: characters = [], isLoading, error } = useCharacters(familyId);
  const {
    data: storyGroups = [],
    isLoading: storyLoading,
    error: storyError,
  } = useStoryCharacters(familyId);

  // Apply client-side sorting and filtering (library tab)
  const filteredCharacters = useMemo(() => {
    let result = [...characters];

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (character) =>
          character.name.toLowerCase().includes(query) ||
          character.physicalDescription?.toLowerCase().includes(query) ||
          character.personalityTraits?.toLowerCase().includes(query) ||
          character.specialAbilities?.toLowerCase().includes(query)
      );
    }

    result.sort((a, b) => {
      let comparison: number;

      switch (sortBy) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'usageCount':
          comparison = a.usageCount - b.usageCount;
          break;
        case 'createdAt':
        default:
          comparison = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
          break;
      }

      return sortOrder === 'desc' ? -comparison : comparison;
    });

    return result;
  }, [characters, searchQuery, sortBy, sortOrder]);

  const storyCount = useMemo(
    () => storyGroups.reduce((sum, g) => sum + g.characters.length, 0),
    [storyGroups],
  );

  const sortOptions: SortOption[] = useMemo(() => [
    { value: 'createdAt', label: t('filters.sortByCreated') },
    { value: 'usageCount', label: t('filters.sortByUsage') },
    { value: 'name', label: t('filters.sortByName') },
  ], [t]);

  const sortLabels = useMemo(() => ({
    sortBy: t('filters.sortBy'),
    sortOrder: t('filters.sortOrder'),
    asc: t('filters.sortAsc'),
    desc: t('filters.sortDesc'),
  }), [t]);

  const handleCreateClick = () => {
    setEditingCharacter(null);
    setIsFormOpen(true);
  };

  const handleEditClick = (character: Character) => {
    setEditingCharacter(character);
    setIsFormOpen(true);
  };

  const handleDeleteSuccess = () => {
    // React Query auto-invalidates via EventRouter SSE
  };

  const handleCopyFromStory = (id: string) => {
    if (copyingId || copiedIds.has(id)) return;
    setCopyingId(id);
    copyCharacterFromStory(id)
      .then((res) => {
        if (res.success) {
          setCopiedIds((s) => new Set(s).add(id));
          void queryClient.invalidateQueries({ queryKey: characterKeys.all });
        }
      })
      .finally(() => setCopyingId(null));
  };

  const handleFormSuccess = (character: Character) => {
    // React Query auto-invalidates via EventRouter SSE
    setEditingCharacter(character);
    // Scroll the underlying page to the top so the user can see the new card
    // (default sort is createdAt desc → it appears at position 0). The Sheet
    // stays open after creation so generation progress is visible on the card.
    if (typeof window !== 'undefined') {
      requestAnimationFrame(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      });
    }
  };

  // ─── Library tab ─────────────────────────────────────────────────────
  const renderLibrary = () => {
    if (isLoading && characters.length === 0) {
      return (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      );
    }

    if (error && characters.length === 0) {
      return (
        <Alert variant="destructive">
          <AlertDescription>{error.message}</AlertDescription>
        </Alert>
      );
    }

    return (
      <div className="space-y-3 sm:space-y-6">
        <ListFilters
          searchValue={searchValue}
          onSearchChange={setSearchValue}
          searchPlaceholder={t('search')}
          searchTestId="character-search-input"
        >
          <SortDropdown
            sortBy={sortBy}
            sortOrder={sortOrder}
            options={sortOptions}
            onSortByChange={(v) => setSortBy(v as 'createdAt' | 'usageCount' | 'name')}
            onSortOrderChange={setSortOrder}
            labels={sortLabels}
            testId="character-sort-button"
          />
        </ListFilters>

        {filteredCharacters.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="mb-4 rounded-full bg-muted p-6">
              <Search className="h-12 w-12 text-muted-foreground" />
            </div>
            <h3 className="mb-2 text-lg font-semibold">{t('emptyTitle')}</h3>
            <p className="mb-6 max-w-md text-muted-foreground">
              {searchValue ? t('emptyFiltered') : t('emptyMessage')}
            </p>
            {!searchValue && (
              <Button onClick={handleCreateClick} size="lg">
                <Plus className="h-4 w-4" />
                {t('createFirst')}
              </Button>
            )}
          </div>
        ) : (
          <div data-testid="characters-list" className="grid gap-3 sm:gap-4 grid-cols-1 sm:grid-cols-2">
            {filteredCharacters.map((character) => (
              <CharacterCard
                key={character.id}
                character={character}
                onEdit={handleEditClick}
                onDelete={handleDeleteSuccess}
                // System characters (seeded from admin templates) are used as-is —
                // not editable/deletable.
                readOnly={character.creationMethod === 'template'}
              />
            ))}
          </div>
        )}
      </div>
    );
  };

  // ─── "From stories" tab ──────────────────────────────────────────────
  const renderStory = () => {
    if (storyLoading && storyGroups.length === 0) {
      return (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      );
    }

    if (storyError && storyGroups.length === 0) {
      return (
        <Alert variant="destructive">
          <AlertDescription>{storyError.message}</AlertDescription>
        </Alert>
      );
    }

    if (storyGroups.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <div className="mb-4 rounded-full bg-muted p-6">
            <BookOpen className="h-12 w-12 text-muted-foreground" />
          </div>
          <p className="max-w-md text-muted-foreground">{t('tabs.storyEmpty')}</p>
        </div>
      );
    }

    // Filter by search (name/description), drop emptied groups.
    const q = storySearch.trim().toLowerCase();
    const filteredGroups = !q
      ? storyGroups
      : storyGroups
          .map((group) => ({
            ...group,
            characters: group.characters.filter(
              (c) =>
                c.name.toLowerCase().includes(q) ||
                c.physicalDescription?.toLowerCase().includes(q),
            ),
          }))
          .filter((group) => group.characters.length > 0);
    const filteredCount = filteredGroups.reduce((sum, g) => sum + g.characters.length, 0);

    // Reveal groups incrementally by character count (data is already loaded —
    // this is a client-side "show more", no extra requests). Partial-fill the
    // last group so the cut lands exactly at the visible count.
    const shownGroups: typeof storyGroups = [];
    let budget = storyVisible;
    for (const group of filteredGroups) {
      if (budget <= 0) break;
      const slice = group.characters.slice(0, budget);
      shownGroups.push({ ...group, characters: slice });
      budget -= slice.length;
    }
    const hasMore = filteredCount > storyVisible;

    const searchBox = (
      <div className="relative">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={storySearch}
          onChange={(e) => setStorySearch(e.target.value)}
          placeholder={t('search')}
          className="pl-8"
          data-testid="story-search-input"
        />
        {storySearch && (
          <button
            type="button"
            onClick={() => setStorySearch('')}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            aria-label={t('search')}
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    );

    if (filteredGroups.length === 0) {
      return (
        <div className="space-y-4">
          {searchBox}
          <p className="py-6 text-center text-sm text-muted-foreground">{t('emptyFiltered')}</p>
        </div>
      );
    }

    return (
      <div data-testid="story-characters-list" className="space-y-6">
        {searchBox}
        {shownGroups.map((group) => (
          <section key={group.bookId} className="space-y-3">
            <h3 className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <BookOpen className="h-4 w-4 shrink-0" />
              <span className="truncate">
                {group.bookTitle
                  ? t('tabs.fromBook', { title: group.bookTitle })
                  : t('tabs.fromStories')}
              </span>
            </h3>
            <div className="grid gap-3 sm:gap-4 grid-cols-1 sm:grid-cols-2">
              {group.characters.map((character) => {
                const copied = copiedIds.has(character.id);
                return (
                  <div key={character.id} className="space-y-1.5">
                    <CharacterCard character={character} readOnly />
                    <Button
                      size="sm"
                      variant={copied ? 'secondary' : 'outline'}
                      className="w-full"
                      disabled={!!copyingId || copied}
                      onClick={() => handleCopyFromStory(character.id)}
                      data-testid={`story-copy-${character.id}`}
                    >
                      {copyingId === character.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : copied ? (
                        <><Check className="h-4 w-4" /> {t('addedToLibrary')}</>
                      ) : (
                        <><Plus className="h-4 w-4" /> {t('addToLibrary')}</>
                      )}
                    </Button>
                  </div>
                );
              })}
            </div>
          </section>
        ))}

        {hasMore && (
          <div className="flex justify-center pt-2">
            <Button
              variant="outline"
              data-testid="story-load-more"
              onClick={() => setStoryVisible((v) => v + 20)}
            >
              {t('tabs.loadMore')}
            </Button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div data-testid="characters-library-page" className="space-y-3 sm:space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <h1 className="text-lg sm:text-2xl font-bold text-foreground">{t('title')}</h1>
          <p className="text-xs sm:text-sm text-muted-foreground">
            {filteredCharacters.length === 0
              ? t('emptyTitle')
              : t('found', { count: filteredCharacters.length })}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {/* Promote a hero from a story into the fixed library (story characters
              are internal — surfaced here only as a copy source). */}
          {storyCount > 0 && (
            <Button
              variant="outline"
              data-testid="character-from-story-button"
              onClick={() => setStoryPickerOpen(true)}
            >
              <BookOpen className="w-4 h-4" />
              <span className="hidden sm:inline">{t('fromStoryButton')}</span>
            </Button>
          )}
          <Button data-testid="character-create-button" onClick={handleCreateClick}>
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">{t('createButton')}</span>
          </Button>
        </div>
      </div>

      <div className="mt-4 sm:mt-6">{renderLibrary()}</div>

      {/* "Add from a story" picker */}
      <Dialog open={storyPickerOpen} onOpenChange={setStoryPickerOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t('fromStoryTitle')}</DialogTitle>
            <DialogDescription>{t('fromStoryDescription')}</DialogDescription>
          </DialogHeader>
          {renderStory()}
        </DialogContent>
      </Dialog>

      {/* CharacterForm is ALWAYS mounted — never unmounted by loading/error states.
          This preserves form state (generating spinner, etc.) during parent refetches. */}
      <CharacterForm
        open={isFormOpen}
        onOpenChange={setIsFormOpen}
        character={editingCharacter}
        onSuccess={handleFormSuccess}
      />
    </div>
  );
}
